import { MetaCarteirasFicFidcsChildPosition } from 'src/services/meta-carteiras-fic-fidcs';

export interface FidcManager {
  id?: string | null;
  cnpj?: string | null;
  name?: string | null;
  startDate?: string | null;
}

export interface FidcUnderlyingAssetsAndManagers {
  operationId: string;
  underlyingAssets?: string | null;
  managers?: FidcManager[] | null;
}

export interface FicFidcCompositionItem {
  key: string;
  label: string;
  value: number;
  percentage: number;
}

export interface FicFidcCompositionData {
  assets: FicFidcCompositionItem[];
  managers: FicFidcCompositionItem[];
}

const ASSET_NOT_INFORMED = 'Ativo-lastro não informado';
const MANAGER_NOT_INFORMED = 'Gestor não informado';

function normalizeLabel(value: string) {
  return value.trim().replace(/\s+/g, ' ');
}

function normalizeKey(value: string) {
  return normalizeLabel(value).toLocaleLowerCase('pt-BR');
}

function getDateTimestamp(value?: string | null) {
  if (!value) return undefined;

  const brazilianDateMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);

  if (brazilianDateMatch) {
    const [, day, month, year] = brazilianDateMatch;
    const date = new Date(
      Date.UTC(Number(year), Number(month) - 1, Number(day)),
    );

    if (
      date.getUTCFullYear() === Number(year) &&
      date.getUTCMonth() === Number(month) - 1 &&
      date.getUTCDate() === Number(day)
    ) {
      return date.getTime();
    }

    return undefined;
  }

  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp) ? undefined : timestamp;
}

function getMostRecentManager(managers?: FidcManager[] | null) {
  const availableManagers = managers ?? [];

  if (availableManagers.length === 1) return availableManagers[0];

  const managersWithValidDate = availableManagers.filter((manager) => {
    return getDateTimestamp(manager.startDate) !== undefined;
  });

  if (!managersWithValidDate.length) return undefined;

  return managersWithValidDate.reduce((mostRecentManager, manager) => {
    const managerTimestamp = getDateTimestamp(manager.startDate);
    const mostRecentManagerTimestamp = getDateTimestamp(
      mostRecentManager.startDate,
    );

    return managerTimestamp !== undefined &&
      mostRecentManagerTimestamp !== undefined &&
      managerTimestamp > mostRecentManagerTimestamp
      ? manager
      : mostRecentManager;
  });
}

function getManagerCategory(manager?: FidcManager) {
  if (!manager) {
    return { key: MANAGER_NOT_INFORMED, label: MANAGER_NOT_INFORMED };
  }

  const label = normalizeLabel(manager.name ?? '') || MANAGER_NOT_INFORMED;
  const identifier = manager.cnpj || manager.id || label;

  return {
    key: `manager-${normalizeKey(identifier)}`,
    label,
  };
}

function addValue(
  categories: Map<string, { label: string; value: number }>,
  key: string,
  label: string,
  value: number,
) {
  const currentCategory = categories.get(key);

  categories.set(key, {
    label: currentCategory?.label ?? label,
    value: (currentCategory?.value ?? 0) + value,
  });
}

function addNotInformedCategories(
  assets: Map<string, { label: string; value: number }>,
  managers: Map<string, { label: string; value: number }>,
  value: number,
) {
  addValue(assets, normalizeKey(ASSET_NOT_INFORMED), ASSET_NOT_INFORMED, value);
  addValue(managers, MANAGER_NOT_INFORMED, MANAGER_NOT_INFORMED, value);
}

function toCompositionItems(
  categories: Map<string, { label: string; value: number }>,
) {
  const total = Array.from(categories.values()).reduce(
    (sum, category) => sum + category.value,
    0,
  );

  if (!total) return [];

  return Array.from(categories.entries())
    .map(([key, category]) => ({
      key,
      label: category.label,
      value: category.value,
      percentage: (category.value / total) * 100,
    }))
    .sort((first, second) => second.value - first.value);
}

export function mapFicFidcComposition(
  positions: MetaCarteirasFicFidcsChildPosition[],
  underlyingAssetsAndManagers: FidcUnderlyingAssetsAndManagers[],
): FicFidcCompositionData {
  const dataByOperationId = new Map(
    underlyingAssetsAndManagers.map((item) => [String(item.operationId), item]),
  );
  const assets = new Map<string, { label: string; value: number }>();
  const managers = new Map<string, { label: string; value: number }>();

  positions.forEach((position) => {
    if (position.vlMercPosFinal === null) return;

    const value = position.vlMercPosFinal;

    if (!Number.isFinite(value) || value <= 0) return;

    const operationData = position.operationId
      ? dataByOperationId.get(String(position.operationId))
      : undefined;

    if (!operationData) {
      addNotInformedCategories(assets, managers, value);
      return;
    }

    const assetLabel =
      normalizeLabel(operationData.underlyingAssets ?? '') ||
      ASSET_NOT_INFORMED;
    const manager = getManagerCategory(
      getMostRecentManager(operationData.managers),
    );

    addValue(assets, normalizeKey(assetLabel), assetLabel, value);
    addValue(managers, manager.key, manager.label, value);
  });

  return {
    assets: toCompositionItems(assets),
    managers: toCompositionItems(managers),
  };
}
